import React, { useState, Children, cloneElement } from 'react'
import { css, cx } from '@linaria/core'
import { ArrowCaret } from '@fs/zion-icon'
import PropTypes from 'prop-types'
import { boolAttr, oneOfComponentType } from '@fs/zion-frontend-friends'
import ListItem, { listItemCss } from './ListItem'
import Checkbox from '../checkbox'
import { Expander, ExpanderContent } from '../expander'
import { FillerMedia } from './mediaTypes'
import ListItemCore from './ListItemCore'
import { colors } from '../theme'

const expandableListItemCss = css`
  transition: border-color 0.5s;
  margin-bottom: -1px;
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;

  &[data-expanded] {
    border-color: ${colors.transparent.gray10};
    & + & {
      border-top-color: transparent; /* This keeps stacked borders from being double-darkened */
    }
  }
`

const contentCss = css`
  padding: 0 12px;
`

function ControlledExpandableListItem({
  children,
  startElement,
  open,
  setOpen,
  lastChild,
  noKeyline = false,

  /** [SCRUBBED]: Don't pass on to children */
  endElement,
  to,
  external,

  ...props
}) {
  return (
    <Expander open={open} setOpen={setOpen}>
      <li className={cx(listItemCss, expandableListItemCss)} data-expanded={boolAttr(open)}>
        <ListItemCore
          {...props}
          interactable
          aria-expanded={open ? 'true' : 'false'}
          onClick={() => setOpen((o) => !o)}
          startElement={startElement}
          endElement={<ListItem.Icon Icon={ArrowCaret} direction={open ? 'up' : 'down'} />}
        />
        <ExpanderContent as="ul" className={contentCss}>
          {Children.map(children, (child) =>
            cloneElement(child, {
              startElement: startElement && !noKeyline ? <FillerMedia element={startElement} /> : undefined,
              onClick: open ? child.props.onClick : undefined, // Remove interactability when sublist is collapsed so tabbing/accessibility do not have issues
            })
          )}
        </ExpanderContent>
      </li>
    </Expander>
  )
}

function UncontrolledExpandableListItem({ initiallyOpen = false, ...props }) {
  const [open, setOpen] = useState(initiallyOpen)
  return <ControlledExpandableListItem {...{ ...props, open, setOpen }} />
}

export default function ExpandableListItem(props) {
  const { setOpen, onClick } = props
  return onClick || setOpen ? (
    <ControlledExpandableListItem {...props} />
  ) : (
    <UncontrolledExpandableListItem {...props} />
  )
}

export const meta = {
  name: 'ExpandableListItem',
}

ExpandableListItem.meta = meta

ExpandableListItem.propTypes = {
  /** If control over the open state is needed, provide the open state through this prop. */
  open: PropTypes.bool,

  /** If control over the open state is needed, provide the open state setter through this prop. */
  setOpen: PropTypes.func,

  /** If true, the expandable list will be expanded by default. */
  initiallyOpen: PropTypes.bool,

  /** Displays the number provided as a count next to the primary text. */
  badgeCount: PropTypes.number,

  /** If true, reduces the vertical padding on the list item. */
  dense: PropTypes.bool,

  /** If true, disables the item. */
  disabled: PropTypes.bool,

  /** Add emphasis to this item */
  emphasized: PropTypes.bool,

  /** Displays media on the start side of the list item. Must be one of these: ListItem.Icon, ListItem.Avatar, ListItem.MultiAvatar, ListItem.Image, ListItem.FillerMedia */
  startElement: oneOfComponentType([
    ListItem.Icon,
    ListItem.Avatar,
    ListItem.MultiAvatar,
    ListItem.Image,
    ListItem.FillerMedia,
    Checkbox,
  ]),

  /** Adds overline text to the list item's text block. */
  overline: PropTypes.string,

  /** Primary line of text. */
  primaryText: PropTypes.node.isRequired,

  /** Secondary line of text. */
  secondaryText: PropTypes.node,

  /** Show this item as selected */
  selected: PropTypes.bool,

  /** If true, it removed the filler space (if present) to the start of the component. To be used when ExpandableListItem has a startElement, but you don't want the children to align with it. */
  noKeyline: PropTypes.bool,
}
